/**
 * Context de Autenticação Multi-Tenant
 * Gerencia estado global de autenticação, organização e grupo
 */

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { authService, UserResponse, LoginRequest, Organization, GroupInfo, GroupOrganization } from '../services/authService';
import { organizationService } from '../services/organizationService';

interface AuthContextData {
  user: UserResponse | null;
  organization: Organization | null;
  organizationName: string;
  group: GroupInfo | null;
  groupOrganizations: GroupOrganization[];
  activeOrganizationId: string | null;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isGroupUser: boolean;
  hasRole: (role: string) => boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  switchOrganization: (organizationId: string) => void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

interface AuthProviderProps {
  children: ReactNode;
}

const ACTIVE_ORG_KEY = 'awflux_active_organization_id';

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserResponse | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [organizationName, setOrganizationName] = useState<string>('AWFlux');
  const [group, setGroup] = useState<GroupInfo | null>(null);
  const [groupOrganizations, setGroupOrganizations] = useState<GroupOrganization[]>([]);
  const [activeOrganizationId, setActiveOrganizationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Troca de organização ativa (para usuários de grupo)
  const switchOrganization = useCallback((organizationId: string) => {
    // Pages with unsaved work can explicitly cancel before the active tenant
    // changes. With no listener this preserves the existing switch behavior.
    if (!window.dispatchEvent(new CustomEvent('auth:before-org-switch', {
      cancelable: true, detail: { organizationId },
    }))) return;
    setActiveOrganizationId(organizationId);
    localStorage.setItem(ACTIVE_ORG_KEY, organizationId);

    // Atualizar o nome da organização exibido
    const targetOrg = groupOrganizations.find(o => o.id === organizationId);
    if (targetOrg) {
      setOrganizationName(targetOrg.name);
      // Atualizar organização no localStorage também (para compatibilidade)
      const orgObj: Organization = {
        id: targetOrg.id,
        name: targetOrg.name,
        slug: targetOrg.slug,
        logo_url: targetOrg.logo_url,
      };
      setOrganization(orgObj);
      authService.setOrganization(orgObj);
    }

    // Disparar evento para componentes reagirem à mudança de organização
    window.dispatchEvent(new CustomEvent('auth:org-switch', { detail: { organizationId } }));
  }, [groupOrganizations]);

  // Carregar usuário, organização e grupo do localStorage na inicialização
  useEffect(() => {
    const loadUser = async () => {
      try {
        const storedUser = authService.getUser();
        const storedOrg = authService.getOrganization();
        const storedGroup = authService.getGroup();
        const storedGroupOrgs = authService.getGroupOrganizations();
        const storedActiveOrgId = localStorage.getItem(ACTIVE_ORG_KEY);
        const token = authService.getToken();

        if (storedUser && token) {
          setUser(storedUser);
          if (storedUser.role === 'partner') {
            localStorage.removeItem(ACTIVE_ORG_KEY);
            return;
          }

          // Carregar dados de grupo se existirem
          if (storedGroup) {
            setGroup(storedGroup);
          }
          if (storedGroupOrgs.length > 0) {
            setGroupOrganizations(storedGroupOrgs);
          }

          // Restaurar organização ativa
          if (storedActiveOrgId && storedGroupOrgs.length > 0) {
            setActiveOrganizationId(storedActiveOrgId);
            const activeOrg = storedGroupOrgs.find(o => o.id === storedActiveOrgId);
            if (activeOrg) {
              setOrganizationName(activeOrg.name);
              const orgObj: Organization = {
                id: activeOrg.id,
                name: activeOrg.name,
                slug: activeOrg.slug,
                logo_url: activeOrg.logo_url,
              };
              setOrganization(orgObj);
            }
          } else if (storedOrg) {
            setOrganization(storedOrg);
            setOrganizationName(storedOrg.name);
            setActiveOrganizationId(storedOrg.id);
          } else if (storedUser.organization_id) {
            setActiveOrganizationId(storedUser.organization_id);
            // Buscar organização completa com logo da API
            try {
              const orgDetails = await organizationService.getOrganizationDetails(
                storedUser.organization_id,
                token
              );
              const fullOrg: Organization = {
                id: orgDetails.id,
                name: orgDetails.name,
                slug: storedUser.organization_id,
                logo_url: orgDetails.logo_url,
              };
              setOrganization(fullOrg);
              setOrganizationName(orgDetails.name);
              authService.setOrganization(fullOrg);
            } catch (error) {
              console.error('Erro ao buscar organização:', error);
            }
          }
        }
      } catch (error) {
        console.error('Erro ao carregar usuário:', error);
        authService.logout();
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  const login = async (credentials: LoginRequest) => {
    setLoading(true);
    try {
      const response = await authService.login(credentials);
      setUser(response.user);
      setOrganization(null);
      setOrganizationName('AWFlux');
      setGroup(null);
      setGroupOrganizations([]);
      setActiveOrganizationId(null);

      // Carregar dados de grupo se existirem
      if (response.group) {
        setGroup(response.group);
      }
      if (response.organizations && response.organizations.length > 0) {
        setGroupOrganizations(response.organizations);

        // Definir organização ativa: preferir a organization_id do usuário, senão primeira do grupo
        const defaultOrgId = response.user.organization_id || response.organizations[0].id;
        setActiveOrganizationId(defaultOrgId);
        localStorage.setItem(ACTIVE_ORG_KEY, defaultOrgId);

        const activeOrg = response.organizations.find(o => o.id === defaultOrgId);
        if (activeOrg) {
          const orgObj: Organization = {
            id: activeOrg.id,
            name: activeOrg.name,
            slug: activeOrg.slug,
            logo_url: activeOrg.logo_url,
          };
          setOrganization(orgObj);
          setOrganizationName(activeOrg.name);
          authService.setOrganization(orgObj);
        }
      } else if (response.organization) {
        setOrganization(response.organization);
        setOrganizationName(response.organization.name);
        setActiveOrganizationId(response.organization.id);
      } else if (response.user.organization_id) {
        setActiveOrganizationId(response.user.organization_id);
        // Buscar organização completa com logo da API
        try {
          const orgDetails = await organizationService.getOrganizationDetails(
            response.user.organization_id,
            response.access_token
          );
          const fullOrg: Organization = {
            id: orgDetails.id,
            name: orgDetails.name,
            slug: response.user.organization_id,
            logo_url: orgDetails.logo_url,
          };
          setOrganization(fullOrg);
          setOrganizationName(orgDetails.name);
          authService.setOrganization(fullOrg);
        } catch (error) {
          console.error('Erro ao buscar organização:', error);
          setOrganizationName('AWFlux');
        }
      }
      
      // Disparar evento customizado para notificar outros componentes (ex: CamerasContext)
      window.dispatchEvent(new CustomEvent('auth:login', { detail: { user: response.user } }));
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    authService.logout();
    localStorage.removeItem(ACTIVE_ORG_KEY);
    setUser(null);
    setOrganization(null);
    setOrganizationName('AWFlux');
    setGroup(null);
    setGroupOrganizations([]);
    setActiveOrganizationId(null);
  };

  const refreshUser = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
      authService.setUser(currentUser);
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      logout();
    }
  };

  const hasRole = (role: string): boolean => {
    return authService.hasRole(role);
  };

  const value: AuthContextData = {
    user,
    organization,
    organizationName,
    group,
    groupOrganizations,
    activeOrganizationId,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.is_superuser === true,
    isGroupUser: !!group && groupOrganizations.length > 0,
    hasRole,
    login,
    logout,
    refreshUser,
    switchOrganization,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }

  return context;
}
